import React from "react"
import { observer } from "mobx-react-lite"
import { Link, useNavigate } from "react-router-dom"
import clsx from "clsx"

import Icon from "@components/ui/Icon/Icon"
import { Option } from "@framework/types/utils"
import { useStore } from "@store/index"
import { useAppConfig } from "@components/theme/AppConfigProvider"
import Logo from "@components/prototypes/Logo/Logo"
import Button from "@components/ui/Button/Button"
import useNavigationScope from "@components/layout/NavigationScopeContext/useNavigationScope"
import Text from "@components/ui/Typography/Text"
import useClickOutside from "@components/hooks/useOutsideClick"
import { disarmRefTimer } from "@utils/react"

import UserSettingsMenu from "./UserSettingsMenu"
import UIThemeMenu from "./UIThemeMenu"
import HistoryNavItem from "./HistoryNavItem"
import NavIcon from "./NavItem/NavIcon"
import ExpandableNavItem from "./NavItem/ExandableNavItem"

import styles from "./Sidebar.module.sass"

type NavItemBadgeProps = {
  name: string
}

const SIDEBAR_AUTO_CLOSE_DELAY_MILLIS = 1200
const SIDEBAR_AUTO_OPEN_DELAY_MILLIS = 300

const NavItemBadge: React.FC<NavItemBadgeProps> = observer(
  ({ name }) =>
    // const {
    //   updateStore: { hasChanges },
    // } = useStore()
    // if (name === "/updates" && hasChanges)
    //   return <NotificationBadge className={styles.loader} />
    null
)

export const Sidebar: React.FC = observer(() => {
  const navigate = useNavigate()

  const theme = useAppConfig()

  const { restrictionsStore: access } = useStore()

  const {
    allowedNavOptions: menuOptions,
    allowedProfileOptions: profileOptions,
  } = useNavigationScope()

  const [selectedOption, setSelectedOption] = React.useState<Option | null>()
  const [open, setOpen] = React.useState(false)
  const [showExpandArrow, setShowExpandArrow] = React.useState(false)

  const containerRef = React.useRef<HTMLDivElement | null>(null)
  const openTimer = React.useRef<NodeJS.Timeout | null>(null)
  const leaveTimer = React.useRef<NodeJS.Timeout | null>(null)

  useClickOutside({
    element: containerRef,
    disabled: !open,
    options: true,
    onClickOutside: () => {
      setOpen(false)
      disarmRefTimer(openTimer)
      disarmRefTimer(leaveTimer)
    },
  })

  const handleMouseLeave = () => {
    setShowExpandArrow(false)

    disarmRefTimer(openTimer)

    if (leaveTimer.current != null) return

    leaveTimer.current = setTimeout(() => {
      setOpen(false)
      disarmRefTimer(leaveTimer)
    }, SIDEBAR_AUTO_CLOSE_DELAY_MILLIS)
  }

  const handleMouseEnterOpenTrigger = () => {
    if (openTimer.current != null) return

    openTimer.current = setTimeout(() => {
      setOpen(true)
      setShowExpandArrow(false)
      disarmRefTimer(openTimer)
    }, SIDEBAR_AUTO_OPEN_DELAY_MILLIS)
  }

  const handleMouseEnter = () => {
    setShowExpandArrow(true)
    disarmRefTimer(leaveTimer)
  }

  const handleMouseMove = () => {
    setShowExpandArrow(!open)
  }

  const handleExpandClick = () => {
    setOpen(true)
    setShowExpandArrow(false)
  }

  const memoizedMenuOptions = React.useMemo(
    () =>
      menuOptions.map((item) =>
        open ? (
          <ExpandableNavItem
            active={item === selectedOption}
            key={item.name}
            isRoot
            option={item}
            parentOption={item}
            setSelectedOption={setSelectedOption}
            setSideBarOpen={setOpen}
          />
        ) : (
          <NavIcon
            key={item.name}
            option={item}
            name={item.name}
            icon={item.icon}
            onClick={() => navigate(item.name)}
            className={styles.navItem}
            mark={<NavItemBadge name={item.name} />}
          />
        )
      ),
    [menuOptions, open, selectedOption]
  )

  const isNesh = theme.copy.logo === "NESH"
  // DKSH renders its logo in brand red (matches the About page).
  const isDKSH = theme.copy.logo?.toUpperCase() === "DKSH"
  return (
    <div
      className={clsx(open ? styles.rootOpen : styles.rootClose)}
      onMouseLeave={handleMouseLeave}
      onMouseEnter={handleMouseEnter}
      onMouseMove={handleMouseMove}
      ref={containerRef}
    >
      <div
        className={styles.hoverStrip}
        onMouseEnter={handleMouseEnterOpenTrigger}
      />
      <div className={styles.header}>
        {open ? (
          <div className={styles.logoOpen}>
            <Link to="/home">
              <Logo
                name={theme.copy.logo}
                height={24}
                withText
                primary={isDKSH}
              />
            </Link>
            <Button size="tiny" variant="text" onClick={() => setOpen(false)}>
              <Icon name="cross" rotateAngle={-90} />
            </Button>
          </div>
        ) : (
          <div
            role="button"
            tabIndex={0}
            className={clsx(styles.logoClose, { [styles.paddingLeft]: isNesh })}
            onClick={() => navigate("/home")}
            onKeyPress={(e) => {
              if (e.key === "Enter" || e.key === " ") {
                setOpen(true)
              }
            }}
          >
            <Logo name={theme.copy.logo} height={24} primary={isDKSH} />
          </div>
        )}
        {showExpandArrow && (
          <Button
            onClick={handleExpandClick}
            size="tiny"
            className={styles.expandArrow}
          >
            <Icon name="arrow-down" rotateAngle={-90} />
          </Button>
        )}
      </div>
      {open ? (
        <div className={styles.expandableMenu}>
          {memoizedMenuOptions}
          <HistoryNavItem expanded />
        </div>
      ) : (
        <div className={styles.menu}>
          {memoizedMenuOptions}
          <HistoryNavItem expanded={false} />
        </div>
      )}

      <div className={styles.footer}>
        {access.canSwitchTheme && <UIThemeMenu showText={open} />}

        <a
          href={theme.support.supportPageURL}
          target="_blank"
          className={styles.support}
          rel="noreferrer"
        >
          <Icon className={styles.helpButton} name="question-mark" />
          {open && (
            <Text className={styles.name} variant="body2" color="inherit">
              Help Me
            </Text>
          )}
        </a>

        <UserSettingsMenu showMore={open} menuOptions={profileOptions} />
      </div>
    </div>
  )
})

export default Sidebar
